<template>
  <div class="product-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('products.title') }}</h2>
      <b-button variant="primary" @click="createProduct">
        <i class="fas fa-plus mr-2"></i>{{ $t('products.create_button') }}
      </b-button>
    </div>

    <CrudTable
      :columns="fields"
      :data-sources="products"
      :pagination="paginationInfo"
      :loader="isLoading"
      list-url="/products"
      @search="handleSearch"
      @filter="handleFilter"
      @refresh="fetchProducts"
    >
      <!-- Name Column -->
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <div>
            <div class="font-weight-bold">{{ data.item.name || 'N/A' }}</div>
            <div class="text-muted small">{{ data.item.user?.full_name || 'N/A' }}</div>
          </div>
        </div>
      </template>

      <!-- Status Column -->
      <template #cell(is_active)="data">
        <div>
          {{ data.item.is_active ? 'Active' : 'Inactive' }}
        </div>
      </template>

      <!-- Actions Column -->
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="editProduct(data.item)"
          >
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="confirmDelete(data.item)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('products.delete_modal_title')"
      @ok="handleDelete"
      ok-variant="danger"
    >
      {{ $t('products.delete_modal_message') }}
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: 'ProductList',
  components: {
    CrudTable
  },

  data() {
    return {
      products: [],
      isLoading: false,
      showDeleteModal: false,
      selectedProduct: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'created_at',
      sortDesc: true,

      filters: {
        search: '',
        status: null
      },
      fields: [
        { key: 'name', label: this.$t('products.table_header_name'), sortable: true, sortDirection: 'asc' },
        { key: 'is_active', label: this.$t('products.table_header_status'), sortable: true, sortDirection: 'asc' },
        { key: 'actions', label: this.$t('common.actions') }
      ],
      statusOptions: [
        { value: null, text: this.$t('products.status_all') },
        { value: 1, text: this.$t('products.status_active') },
        { value: 0, text: this.$t('products.status_inactive') }
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0
      }
    }
  },

  methods: {
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchProducts();
    },

    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchProducts();
    },

    createProduct() {
      this.$router.push('/admin/products/create');
    },

    editProduct(product) {
      this.$router.push(`/admin/products/${product.id}/edit`);
    },

    confirmDelete(product) {
      this.selectedProduct = product;
      this.showDeleteModal = true;
    },

    async handleDelete() {
      try {
        await request({
          method: 'delete',
          url: `/products/${this.selectedProduct.id}`
        });
        this.fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    },

    async fetchProducts() {
      try {
        this.isLoading = true;
        const response = await request({
          method: 'get',
          url: '/products',
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters
          }
        });

        if (response.data && response.data.data) {
          this.products = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0
            };
          }
        } else {
          this.products = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0
          };
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        this.products = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0
        };
      } finally {
        this.isLoading = false;
      }
    }
  },

  created() {
    this.fetchProducts();
  }
}
</script>

<style scoped>
.product-list {
  padding: 20px;
}
</style> 